import {
  isTelemetryStarted,
  recordAnalyticsEvent,
  startTelemetry
} from '../../telemetry/otel';
import {
  getOtelAuthorization,
  getOtelEndpoint,
  isOtelConfigured
} from '../../telemetry/config';
import { startSession, endSession } from '../../telemetry/tracing';

/**
 * Start OTel + pwa.session as soon as configured (no cookie consent required).
 * Idempotent — safe from index, App, and activateAnalytics.
 */
export function ensurePaperflyOtel(requestId) {
  if (!isOtelConfigured()) {
    return false;
  }

  startTelemetry();
  startSession({
    'app.env': process.env.REACT_APP_ENV || 'local',
    ...(requestId ? { 'app.boot_request_id': String(requestId) } : {})
  });

  if (!window.__paperflyOtelPagehideBound) {
    window.__paperflyOtelPagehideBound = true;
    window.addEventListener('pagehide', () => {
      endSession('ok', { 'app.end_reason': 'pagehide' });
    });
  }

  return isTelemetryStarted();
}

/**
 * Cookie consent for GA / marketing only. OTel starts via ensurePaperflyOtel
 * independently (as early as index/App boot).
 */
export function activateAnalytics(requestId) {
  if (window.analyticsDisabled === undefined || window.consentGranted) return;

  if (window.analyticsDisabled === true) {
    console.debug('Analytics disabled!');
    return;
  }

  window.consentGranted = true;
  ensurePaperflyOtel(requestId);

  // GA initialize is currently disabled — do not set analyticsReady for ReactGA.
  window.analyticsReady = false;

  if (process.env.REACT_APP_ENV === 'production') {
    if (typeof window.consentGrantedAnalytics === 'function') {
      window.consentGrantedAnalytics();
    }
  }

  gae('TELEMETRY_CONSENT', true, requestId);

  if (!isTelemetryStarted()) {
    console.warn('[otel] not started', {
      configured: isOtelConfigured(),
      endpoint: getOtelEndpoint() || '(empty)',
      hasToken: Boolean(getOtelAuthorization())
    });
  }
}

function globalLog(message) {
  if (!window.globalLog) window.globalLog = '';
  window.globalLog += `[${new Date().toISOString()}] ${message}\r\n`;
}

export function disableAnalytics() {
  window.analyticsReady = false;
  window.analyticsDisabled = true;
}

/** "Back to Home" / "PDF Form" → BACK_TO_HOME / PDF_FORM */
export function toActionSuffix(name) {
  return String(name ?? '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .toUpperCase();
}

/** `SET_CURRENT_ACTION_DOCUMENT` + `FORM` → `SET_CURRENT_ACTION_DOCUMENT.FORM` */
export function toQualifiedAction(base, part) {
  const suffix = toActionSuffix(part);
  return suffix ? `${base}.${suffix}` : base;
}

export function gaPageView(path) {
  const suffix = toActionSuffix(path);
  gae('APP', suffix ? `PAGE.${suffix}` : 'PAGE', undefined, undefined, false);
}

export const gaeError = (message = '') => {
  gae('APP', 'ERROR', message, '', false);
};

export function gaeApp(action, label, value) {
  gae('APP', action, label, value, true);
}

export function gaeUser(action, label, value) {
  gae('USER', action, label, value, false);
}

/** "Back to Home" / "PDFForm" → NAVIGATE.BACK_TO_HOME / NAVIGATE.PDF_FORM */
function toNavigateAction(page) {
  const suffix = toActionSuffix(page);
  return suffix ? `NAVIGATE.${suffix}` : 'NAVIGATE';
}

export function gaeAppNavigate(page) {
  gae('APP', toNavigateAction(page), undefined, undefined, true);
}

export function gaeUserNavigate(page, index) {
  gae('USER', toNavigateAction(page), undefined, index, false);
}

/**
 * Send event to local globalLog and OpenTelemetry (when started).
 * ReactGA is not used until ReactGA.initialize is re-enabled.
 */
export const gae = (category, action, label, eventValue, nonInteraction) => {
  const logMessage =
    category +
    ': ' +
    action +
    (label !== undefined ? '(' + label + ')' : '') +
    (eventValue !== undefined ? '[' + eventValue + ']' : '');

  globalLog(logMessage);
  if (process.env.REACT_APP_ENV !== 'production') {
    console.log('%c ' + logMessage, 'background: #bce0f5');
  }

  recordAnalyticsEvent(category, action, label, eventValue, nonInteraction);
};
